import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  DialogActions,
  CardActions,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../utils/baseUrl";

// Utility function for date formatting
const formatDate = (timestamp) => new Date(timestamp * 1000).toLocaleString();

// Hook to fetch concerns with Firebase Auth state handling
export const useConcerns = () => {
  const [concerns, setConcerns] = useState([]);

  const fetchConcerns = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User is not signed in");
      setConcerns([]);
      return;
    }

    try {
      const token = await user.getIdToken();
      const { data } = await axios.get(`${BASE_URL}/concerns`, {
        headers: { Token: token },
      });
      const formattedData = data
        .map((item) => ({
          ...item,
          updatedAt: formatDate(item.updatedAt),
          parentConcern: item.parentConcern ?? undefined,
        }))
        .sort((a, b) => b.parentConcern?.localeCompare(a.parentConcern));
      setConcerns(formattedData);
    } catch (error) {
      console.error("Failed to fetch concerns:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), fetchConcerns);
    return unsubscribe;
  }, []);

  return [concerns, fetchConcerns];
};

// Dialog component for adding or editing concerns
const ConcernDialog = ({
  open,
  onClose,
  onSave,
  initialData = {},
  existingConcerns,
}) => {
  const [formData, setFormData] = useState(initialData);
  const [image, setImage] = useState(null);
  const [url, setUrl] = useState(initialData.image);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(initialData); // Reset form data when dialog opens
      setUrl(initialData.image);
    }
  }, [open, initialData]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    setUploading(true);
    const storage = getStorage();
    const timestamp = Date.now();
    const storageRef = ref(storage, `concerns/${timestamp}`);
    const uploadTask = uploadBytesResumable(storageRef, image);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Progress function ...
      },
      (error) => {
        // Error function ...
        console.log(error);
      },
      () => {
        // Complete function ...
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log("File available at", downloadURL);
          setUrl(downloadURL);
          setUploading(false);
        });
      }
    );
  };

  const handleSubmit = () => {
    onSave({ ...formData, image: url });
    onClose(); // Close dialog after save
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {formData.id ? "Edit Concern" : "Add New Concern"}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          name="isEnglishTitle"
          label="English Title"
          value={formData.isEnglishTitle || ""}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          margin="dense"
          name="isBengaliTitle"
          label="Bangla Title"
          value={formData.isBengaliTitle || ""}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          name="parentConcern"
          label="Parent Concern (Optional)"
          value={formData.parentConcern || ""}
          select
          onChange={handleChange}
        >
          {[...existingConcerns, { isEnglishTitle: "None", id: null }]
            .filter(
              (concern) =>
                concern.id !== initialData.id && !concern.parentConcern
            )
            .map((concern) => (
              <MenuItem key={concern.id} value={concern.id}>
                {concern.isEnglishTitle}
              </MenuItem>
            ))}
        </TextField>
        {url && <img src={url} alt="Preview" style={{ width: "100%" }} />}
        <TextField
          fullWidth
          margin="dense"
          type="file"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          onChange={handleImageChange}
        />
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={() => handleUpload()}
          loading={uploading}
          disabled={!image}
        >
          Upload
        </LoadingButton>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !url || !formData.isEnglishTitle || !formData.isBengaliTitle
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// Component for confirm deletion dialog
const DeleteConfirmationDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirm Deletion</DialogTitle>
    <DialogContent>Are you sure you want to delete this concern?</DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="primary">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const ConcernList = () => {
  const [concerns, fetchConcerns] = useConcerns();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentConcern, setCurrentConcern] = useState(null);
  const navigate = useNavigate();

  const handleDialogClose = () => setDialogOpen(false);
  const handleDeleteDialogClose = () => setDeleteDialogOpen(false);

  const handleSave = async (formData) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User is not signed in");
      return;
    }
    try {
      const token = await user.getIdToken();
      if (formData.id) {
        // Update existing concern
        await axios.put(`${BASE_URL}/concerns/${formData.id}`, formData, {
          headers: { Token: token },
        });
      } else {
        // Create new concern
        await axios.post(`${BASE_URL}/concerns`, formData, {
          headers: { Token: token },
        });
      }
      // TODO:Refresh concerns list
    } catch (error) {
      console.error("Failed to save concern:", error);
    } finally {
      fetchConcerns();
    }
  };

  const handleDelete = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User is not signed in");
      return;
    }
    try {
      const token = await user.getIdToken();
      await axios.delete(
        `${BASE_URL}/concerns/${currentConcern.id}`,
        { headers: { Token: token } }
      );
      console.log("Delete Concern:", currentConcern);

      fetchConcerns();
    } catch (error) {
      console.error("Error deleting concern:", error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const gridStyle = { display: "flex", width: "20vw" };

  return (
    <div style={{ width: "100%" }}>
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        {concerns.map((concern, index) => (
          <Grid item xs={2} sm={4} md={3} key={index} style={gridStyle}>
            <Card style={{ width: "100%" }}>
              <CardActionArea
                onClick={() => {
                  setCurrentConcern(concern);
                  setDialogOpen(true);
                }}
              >
                <CardMedia
                  component="img"
                  image={concern.image}
                  alt={concern.isEnglishTitle}
                  style={{
                    maxHeight: "200px",
                  }}
                />
                <CardContent>
                  <Typography gutterBottom variant="h5">
                    {concern.isEnglishTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {
                      concerns.find((c) => c.id === concern.parentConcern)
                        ?.isEnglishTitle
                    }
                  </Typography>
                </CardContent>
              </CardActionArea>
              <CardActions style={{ justifyContent: "space-between" }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setCurrentConcern(concern);
                    setDeleteDialogOpen(true);
                  }}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    navigate(`/articles?concern=${concern.id}`, {
                      replace: true,
                    });
                  }}
                >
                  View Articles
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        <Grid item xs={2} sm={4} md={3} style={gridStyle}>
          <Card
            onClick={() => {
              setCurrentConcern(null);
              setDialogOpen(true);
            }}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              color: "#484848",
            }}
          >
            <AddCircleOutlineIcon style={{ fontSize: "4rem" }} />
          </Card>
        </Grid>
      </Grid>
      <ConcernDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        initialData={currentConcern || {}}
        existingConcerns={concerns}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ConcernList;